import { useEffect, useRef, useState } from "react";
import { ArrowUpRightIcon, ChevronDownIcon, CrownIcon } from "lucide-react";
import { Link, useLocation } from "react-router";

import {
  Center,
  Column,
  ErrorText,
  JobPinButton,
  LoadingIndicator,
  ParentSidebarBackButton,
  PlacementTab,
  Row,
  Text,
  TopbarContainer,
  TopbarTitle,
} from "@/components";
import { JobPresenceAvatars } from "@/components/JobPresenceAvatars";
import { JobInternalConfig } from "@jobs/job-internal-config/JobInternalConfig";
import { JobCandidacyCountsByStatus } from "@jobs/JobCandidacyCountsByStatus";
import { JobComments } from "@jobs/JobComments";
import { JobEdit } from "@jobs/JobEdit";
import { JobEvaluationBuilder } from "@jobs/JobEvaluationBuilder";
import { JobFiles } from "@jobs/JobFiles";
import { JobLinksPopover } from "@jobs/JobLinksPopover";
import { JobManagersPopover } from "@jobs/JobManagersPopover";
import { JobNotes } from "@jobs/JobNotes";
import { JobOverview } from "@jobs/JobOverview";
import { JobPipelineHeaderMetrics } from "@jobs/JobPipelineHeaderMetrics";
import { JobPrescreeningQuestions } from "@jobs/JobPrescreeningQuestions";
import { JobSourced } from "@jobs/JobSourced";
import { JobStats } from "@jobs/JobStats";
import { JobStatusDropdown } from "@jobs/JobStatusDropdown";
import { JobPipeline } from "@jobs/pipeline/JobPipeline";
import { Requisitions } from "@jobs/Requisitions";
import { JobIntakeContainer } from "@jobs/job-intake/JobIntakeContainer";
import { StafferHeaderSelect } from "@jobs/pipeline/filters/StafferHeaderSelect";
import { JobSearchV2 } from "@jobs/search-v2/JobSearchV2";
import { StafferAgentPausePopover } from "@jobs/staffer-agent/StafferAgentPausePopover";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { useJobPresence } from "@/hooks/useJobPresence";
import { useSelectedTabFromUrl } from "@/hooks/useSelectedTabFromUrl";
import { useSetTitle } from "@/hooks/useSetTitle";
import { useCurrentJobResponse } from "@/hooks/queries/useCurrentJobResponse";
import { useGetCachedCompaniesSummary } from "@/hooks/sets/useGetCachedCompaniesSummary";
import { useSearchInterests } from "@/hooks/search/useSearchInterests";
import { useSetJob } from "@/hooks/sets/useSetJob";
import { ALL_USERS_KEY } from "@/lib/constants";
import { env } from "@/lib/env";
import { misc } from "@/lib/misc";
import { storage } from "@/lib/storage";
import { isUserTyping } from "@/lib/utils";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { DashboardSelectedUser, JobLiquidity, PipelineNavState } from "@/types";

const JOB_MORE_TABS = [
  { value: "edit", label: "Edit Job" },
  { value: "job-intake", label: "Job Intake" },
  { value: "reqs", label: "Reqs" },
  { value: "placements", label: "Placements" },
  { value: "screening", label: "Pre-Screening Questions" },
  { value: "evaluation", label: "Evaluation Builder" },
  { value: "comments", label: "Comments" },
  { value: "files", label: "Files" },
  { value: "internal-config", label: "Internal Config" },
  { value: "notes", label: "Notes" },
];

const primaryJobTabClass =
  "relative mb-[-3px] h-10 !rounded-none !bg-transparent border-x-0 border-t-0 border-b-[3px] border-b-transparent px-2 pb-2 pt-2.5 text-sm font-medium text-slate-500 shadow-none hover:text-slate-700 data-[state=active]:z-10 data-[state=active]:!rounded-none data-[state=active]:!border-x-0 data-[state=active]:!border-t-0 data-[state=active]:!border-b-[3px] data-[state=active]:!border-b-primary-900 data-[state=active]:!bg-transparent data-[state=active]:!font-semibold data-[state=active]:!text-primary-900 data-[state=active]:!shadow-none dark:data-[state=active]:!bg-transparent dark:data-[state=active]:!text-[#C6D6E2] dark:data-[state=active]:!border-b-[#6F8FAA]";
const SEARCH_V2_TAB_NAME = "Search v2";
export const JobPage = () => {
  const { data: jobResponse, status: jobResponseStatus } =
    useCurrentJobResponse();
  const job = jobResponse?.job;
  const jobInternalConfig = jobResponse?.jobInternalConfig;
  const companies = useGetCachedCompaniesSummary();
  const { tabValue, setTabValue } = useSelectedTabFromUrl({
    defaultValue: "pipeline",
    paramName: "t", // TODO: Should we use "jt" to differentiate from other uses?
  });
  const jobPresenceByJobId = useJobPresence();
  const setJob = useSetJob();

  // A dashboard "Comms to Send" cell deep-links with the staffer view its
  // count was scoped to; honor it and skip the filter-on-self default.
  const location = useLocation();
  const [navStafferView] = useState<DashboardSelectedUser | undefined>(
    () => (location.state as PipelineNavState | null)?.stafferView,
  );
  const [staffer, setStaffer] = useState<DashboardSelectedUser>(
    navStafferView ?? ALL_USERS_KEY,
  );

  const currentCompany = companies.find(
    (company) => company.id === job?.companyId,
  );

  // Tab chip = everyone awaiting search review (interested marks, including
  // the auto-flagged top candidates).
  const { searchInterests = [] } = useSearchInterests(job?.id ?? "");
  const interestedCount = searchInterests.filter(
    (interest) => interest.isInterested,
  ).length;
  // Once the search review was opened (here or on the dashboard), the count
  // chip hides. Snapshot on mount — the review modal writes the storage.
  const [searchReviewViewedJobIds] = useState<Set<string>>(
    () => new Set(storage.getSearchReviewViewedJobIds()),
  );
  const isSearchReviewViewed = !!job && searchReviewViewedJobIds.has(job.id);
  const jobLiquidity = job?.liquidity ?? JobLiquidity.UNKNOWN;

  const searchV2TabOpenedAtRef = useRef<number | null>(null);

  useSetTitle({
    enabled: !!currentCompany && !!job,
    // NOTE: The title won't be updated if the company or job is not found.
    title: `${currentCompany?.name ?? "Company"} | ${job?.title ?? "Job"} | ${misc.toTitleCase(tabValue)}`,
  });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.JOB);
  }, []);

  useEffect(() => {
    if (tabValue !== "search-v2") {
      return;
    }

    searchV2TabOpenedAtRef.current = Date.now();
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.JOB, {
      subScreenName: SEARCH_V2_TAB_NAME,
    });

    return () => {
      if (!searchV2TabOpenedAtRef.current) {
        return;
      }
      const durationSeconds = Math.round(
        (Date.now() - searchV2TabOpenedAtRef.current) / 1000,
      );
      mixpanelUtils.trackTabTime(
        MIXPANEL_SCREENS.JOB,
        SEARCH_V2_TAB_NAME,
        durationSeconds,
      );
      searchV2TabOpenedAtRef.current = null;
    };
  }, [tabValue]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.shiftKey || e.metaKey || e.ctrlKey || e.altKey || isUserTyping())
        return;

      const keyToTab: Record<string, string> = {
        q: "pipeline",
        w: "overview",
        e: "edit",
        s: "search-v2",
      };

      const tab = keyToTab[e.key.toLowerCase()];
      if (tab) {
        e.preventDefault();
        mixpanelUtils.trackKeyboardShortcutUsed(
          `Shift+${e.key.toUpperCase()} (${misc.toTitleCase(tab)})`,
          MIXPANEL_SCREENS.JOB,
        );
        setTabValue(tab);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [setTabValue]);

  const render = () => {
    if (jobResponseStatus === "pending") {
      return (
        <Center className="h-full">
          <LoadingIndicator variant="medium" />
        </Center>
      );
    }

    const isJobStatusError = jobResponseStatus === "error";
    if (!currentCompany || isJobStatusError || !jobResponse || !job) {
      const errorReason = !currentCompany
        ? "Company not found."
        : isJobStatusError
          ? "An error occurred while loading the job."
          : "Job not found.";
      return (
        <Center className="h-full">
          <ErrorText
            errorMessage={`${errorReason} Please let the engineers know if they are not already on it!`}
          />
        </Center>
      );
    }

    return (
      <Column className="relative isolate h-svh">
        <TopbarContainer className="group relative z-10 !border-b-0">
          <Column className="min-w-0 gap-0.5">
            <ParentSidebarBackButton />
            <Row className="min-w-0 items-center gap-1.5">
              <TopbarTitle className="!text-slate-500 dark:!text-[#CCCCCC]">
                <Link
                  className="hover:underline"
                  // TODO: Should really have a util that serves as a source of truth for the routes.
                  to={`/companies`}
                >
                  Companies
                </Link>{" "}
                /{" "}
                <Link
                  className="hover:underline"
                  // TODO: Should really have a util that serves as a source of truth for the routes.
                  to={`/companies/${job.companyId}`}
                >
                  {currentCompany.name}
                </Link>{" "}
                / <span className="text-slate-700">{job.title}</span>
              </TopbarTitle>
              <JobPinButton
                companyId={job.companyId}
                jobId={job.id}
                screen={MIXPANEL_SCREENS.JOB}
                variant="medium"
              />
            </Row>
          </Column>
          <Row className="items-center gap-3">
            <StafferHeaderSelect
              onChange={setStaffer}
              skipSelfDefault={navStafferView !== undefined}
              value={staffer}
            />
            {jobInternalConfig && (
              <StafferAgentPausePopover jobInternalConfig={jobInternalConfig} />
            )}
            <JobManagersPopover
              jobId={job.id}
              managerUserIds={jobResponse.managerUserIds}
            />
            <JobLinksPopover job={job} />
            <JobStatusDropdown
              className="h-8 text-xs"
              dotClassName="h-2 w-2"
              job={job}
              setJob={setJob}
            />
          </Row>
        </TopbarContainer>
        {/* Its own band below the fixed-height topbar so the controls above
            keep their spacing whether or not anyone else is on this job. */}
        <Row className="justify-end gap-2 px-6">
          <JobPresenceAvatars
            presences={jobPresenceByJobId.get(job.id) ?? []}
          />
        </Row>
        <Tabs
          className="flex min-h-0 flex-1 flex-col gap-0 pt-3"
          onValueChange={(newTab) => {
            mixpanelUtils.trackTabView(MIXPANEL_SCREENS.JOB, newTab);
            setTabValue(newTab);
          }}
          value={tabValue}
        >
          <Row className="relative items-end justify-between gap-4 border-b border-slate-200 px-6 pt-4">
            <TabsList className="relative z-10 h-auto gap-6 rounded-none bg-transparent p-0">
              <TabsTrigger className={primaryJobTabClass} value="pipeline">
                Pipeline
              </TabsTrigger>
              <TabsTrigger className={primaryJobTabClass} value="sourced">
                Sourced
              </TabsTrigger>
              <TabsTrigger className={primaryJobTabClass} value="overview">
                Overview
              </TabsTrigger>
              <TabsTrigger className={primaryJobTabClass} value="search-v2">
                <Row className="items-center gap-1.5">
                  <Text className="font-inherit text-sm text-inherit">
                    Search
                  </Text>
                  {interestedCount && !isSearchReviewViewed ? (
                    <Row className="items-center gap-1 rounded-full bg-gradient-to-r from-blue-50 to-blue-100 px-2 py-0 ring-1 ring-blue-100 dark:from-[#27435E] dark:to-[#355477] dark:ring-[#6F8FAA]">
                      <CrownIcon className="h-3 w-3 text-blue-500" />
                      <Text className="text-[11px] font-semibold text-blue-700">
                        {interestedCount}
                      </Text>
                    </Row>
                  ) : null}
                </Row>
              </TabsTrigger>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    aria-label="Open job sections"
                    className="relative mb-[-3px] h-10 w-auto rounded-none border-0 border-b-[3px] border-transparent bg-transparent px-2 pb-2 pt-2.5 text-slate-500 shadow-none hover:bg-transparent hover:text-slate-700"
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <ChevronDownIcon className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-[200px] rounded-lg border border-slate-200 bg-slate-100 px-2 py-2 text-slate-950 shadow-md"
                  sideOffset={8}
                >
                  {JOB_MORE_TABS.map((tab) => (
                    <DropdownMenuItem
                      key={tab.value}
                      className="font-regular cursor-pointer rounded-md px-3 py-2 text-base text-slate-950 focus:bg-slate-200"
                      onSelect={() => {
                        mixpanelUtils.trackTabView(
                          MIXPANEL_SCREENS.JOB,
                          tab.value,
                        );
                        setTabValue(tab.value);
                      }}
                    >
                      {tab.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </TabsList>
            <Row className="relative z-10 items-end gap-8">
              {tabValue === "edit" && (
                <Button
                  className="mb-2 shrink-0 rounded-full"
                  onClick={() => {
                    mixpanelUtils.trackButtonClick(
                      "Create Workable Job",
                      MIXPANEL_SCREENS.JOB,
                    );
                    void navigator.clipboard.writeText(job.id);
                    window.open(env.getWorkableNewJobFormUrl());
                  }}
                  variant="outlineSecondary"
                >
                  <ArrowUpRightIcon className="h-4 w-4" />
                  Create Workable Job
                </Button>
              )}
              <JobPipelineHeaderMetrics
                companyId={job.companyId}
                jobId={job.id}
                jobLiquidity={jobLiquidity}
                jobLiquidityBreakdown={job.liquidityBreakdown}
              />
            </Row>
          </Row>
          <TabsContent
            className="mt-0 min-h-0 flex-1 data-[state=active]:flex data-[state=active]:flex-col"
            value="pipeline"
          >
            <JobPipeline className="p-6" job={job} staffer={staffer} />
          </TabsContent>
          <TabsContent
            className="mt-0 min-h-0 flex-1 data-[state=active]:flex data-[state=active]:flex-col"
            value="sourced"
          >
            <JobSourced className="h-full p-6" job={job} />
          </TabsContent>
          <TabsContent className="mt-0 w-full flex-1" value="overview">
            <Row className="w-full">
              <Column className="min-w-0 flex-1">
                <JobCandidacyCountsByStatus className="p-6" />
                <JobOverview
                  className="p-6"
                  companyName={currentCompany.name}
                  job={job}
                  screenName={MIXPANEL_SCREENS.JOB}
                />
              </Column>
              <JobStats
                className="w-[425px] shrink-0 border-l border-slate-200 p-6"
                job={job}
              />
            </Row>
          </TabsContent>
          <TabsContent
            className="relative z-10 mt-0 min-h-0 flex-1 data-[state=active]:flex data-[state=active]:flex-col"
            value="search-v2"
          >
            <JobSearchV2
              companyName={currentCompany.name}
              isAgentActive={!!jobInternalConfig?.stafferAgentActive}
              job={job}
            />
          </TabsContent>
          <TabsContent className="mt-0" value="edit">
            <JobEdit className="p-10" job={job} />
          </TabsContent>
          <TabsContent className="mt-0" value="job-intake">
            <JobIntakeContainer className="p-6" jobTitle={job.title} />
          </TabsContent>
          <TabsContent className="mt-0" value="reqs">
            <Requisitions className="p-6" job={job} />
          </TabsContent>
          <TabsContent className="mt-0" value="placements">
            <PlacementTab className="p-6" job={job} />
          </TabsContent>
          <TabsContent className="mt-0 min-h-0" value="screening">
            <JobPrescreeningQuestions className="p-6" job={job} />
          </TabsContent>
          <TabsContent className="mt-0 min-h-0" value="notes">
            <JobNotes className="h-full p-6" />
          </TabsContent>
          <TabsContent className="mt-0" value="comments">
            <JobComments className="p-6" />
          </TabsContent>
          <TabsContent className="mt-0 min-h-0" value="files">
            <JobFiles className="h-full p-6" />
          </TabsContent>
          <TabsContent className="mt-0" value="internal-config">
            <JobInternalConfig
              className="p-6"
              jobInternalConfig={jobInternalConfig ?? undefined}
            />
          </TabsContent>
          <TabsContent className="mt-0" value="evaluation">
            <JobEvaluationBuilder
              className="p-6"
              companyName={currentCompany.name}
              jobTitle={job.title}
              jobInternalConfig={jobInternalConfig ?? undefined}
            />
          </TabsContent>
        </Tabs>
      </Column>
    );
  };

  return render();
};
